/**
 * CommandPalette — Cmd+K searchable modal for adding any transform node type.
 *
 * Shows all addable node types from the registry, filtered by keystroke.
 * Arrow keys navigate, Enter selects, Escape closes.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Chip,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Slide,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import {
  Search as SearchIcon,
  AttachMoney as CostIcon,
  KeyboardReturn as EnterIcon,
} from '@mui/icons-material';
import { NODE_TYPE_REGISTRY, type AddNodeType, type NodeTypeDefinition } from './utils/nodeTypeRegistry';
import type { CanAddNodeResult } from './hooks/useNodeSelection';

// ── Transition ───────────────────────────────────────────────────────

const SlideDown = React.forwardRef(function SlideDown(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// ── Search scoring ───────────────────────────────────────────────────

function scoreItem(item: NodeTypeDefinition, tokens: string[]): number {
  if (tokens.length === 0) return 1;
  let score = 0;
  const haystack = [item.label, item.shortLabel, item.category, item.description, ...item.keywords]
    .join(' ')
    .toLowerCase();
  for (const token of tokens) {
    if (haystack.includes(token)) score++;
  }
  return score;
}

// ── Props ────────────────────────────────────────────────────────────

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onAddNode: (type: AddNodeType) => void;
  canAddNode: CanAddNodeResult;
  /** Optional initial filter, e.g. 'transform' when opened via Cmd+T. */
  initialFilter?: string;
}

// ── Component ────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  transform: 'Transform',
  enrich: 'Enrich',
  quality: 'Quality',
};

const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onClose,
  onAddNode,
  canAddNode,
  initialFilter,
}) => {
  const theme = useTheme();
  const [query, setQuery] = useState(initialFilter || '');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery(initialFilter || '');
      setActiveIndex(0);
      // Focus input after dialog animation
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, initialFilter]);

  // Filter and score items
  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const filteredItems = NODE_TYPE_REGISTRY
    .map((item) => ({ item, score: scoreItem(item, tokens) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);

  // Clamp active index
  const clampedIndex = Math.min(activeIndex, Math.max(0, filteredItems.length - 1));

  const handleSelect = useCallback(
    (type: AddNodeType) => {
      if (!canAddNode.allowed) return;
      onClose();
      onAddNode(type);
    },
    [canAddNode, onClose, onAddNode],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selected = filteredItems[clampedIndex];
        if (selected && !selected.comingSoon) handleSelect(selected.type as AddNodeType);
      }
    },
    [filteredItems, clampedIndex, handleSelect],
  );

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.querySelector(`[data-index="${clampedIndex}"]`);
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [clampedIndex]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SlideDown}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'absolute',
          top: 80,
          mx: 'auto',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
      BackdropProps={{
        sx: { bgcolor: 'rgba(0,0,0,0.15)' },
      }}
    >
      <Box sx={{ p: 0 }}>
        {/* Guard banner */}
        {!canAddNode.allowed && (
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="body2" color="warning.main" fontWeight={500}>
              {canAddNode.reason}
            </Typography>
          </Box>
        )}

        {/* Search input */}
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Search transforms... (e.g. sql, python, validate)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              '& fieldset': { border: 'none' },
              px: 2,
              py: 0.5,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Results list */}
        <List
          ref={listRef}
          dense
          sx={{
            maxHeight: 360,
            overflowY: 'auto',
            py: 0.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {filteredItems.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No matching transforms
              </Typography>
            </Box>
          )}
          {filteredItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = index === clampedIndex;
            const isComingSoon = item.comingSoon === true;
            const disabled = !canAddNode.allowed || isComingSoon;

            return (
              <ListItemButton
                key={item.type}
                data-index={index}
                selected={isActive}
                disabled={disabled}
                onClick={() => !isComingSoon && handleSelect(item.type as AddNodeType)}
                onMouseEnter={() => setActiveIndex(index)}
                sx={{
                  px: 2,
                  py: 0.75,
                  opacity: isComingSoon ? 0.55 : 1,
                  '&.Mui-selected': {
                    bgcolor: alpha(item.color, 0.08),
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: alpha(item.color, 0.12),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: alpha(item.color, isComingSoon ? 0.08 : 0.15),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 16, color: item.color }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {item.label}
                      </Typography>
                      {isComingSoon ? (
                        <Chip
                          label="Coming Soon"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.text.disabled, 0.1),
                            color: 'text.secondary',
                          }}
                        />
                      ) : (
                        <>
                          <Chip
                            label={CATEGORY_LABELS[item.category]}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 600,
                              bgcolor: alpha(item.color, 0.1),
                              color: item.color,
                            }}
                          />
                          {item.costImplication && (
                            <Tooltip title="This operation may incur API/LLM costs">
                              <CostIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  }
                  secondary={item.description}
                  secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
                {isActive && !isComingSoon && (
                  <EnterIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 1 }} />
                )}
              </ListItemButton>
            );
          })}
        </List>

        {/* Footer hint */}
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 2,
          }}
        >
          <Typography variant="caption" color="text.disabled">
            ↑↓ navigate
          </Typography>
          <Typography variant="caption" color="text.disabled">
            ⏎ select
          </Typography>
          <Typography variant="caption" color="text.disabled">
            esc close
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
